import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, addDoc, doc, updateDoc, getDoc, getDocs } from 'firebase/firestore';
import { Logo } from '../components/Logo';

// ==================== MÍMIR'S WELL - EPHEMERA CATALOGING ====================
// Named for Mímir's Well (Mímisbrunnr), the well of wisdom where Odin
// sacrificed his eye for knowledge. A fitting home for historical treasures.

// ==================== CONFIGURATION ====================

// Ephemera Categories
const EPHEMERA_CATEGORIES = [
  { value: 'CATH', label: 'Cathedral', collection: 'Cathedral Ephemera' },
  { value: 'CAST', label: 'Castle', collection: 'Castle Ephemera' },
  { value: 'PRIO', label: 'Priory', collection: 'Priory Ephemera' },
  { value: 'ABBE', label: 'Abbey', collection: 'Abbey Ephemera' },
  { value: 'CHUR', label: 'Church', collection: 'Church Ephemera' },
  { value: 'HIST', label: 'Historic Site', collection: 'Historic Site Ephemera' },
  { value: 'GARD', label: 'Garden', collection: 'Garden Ephemera' },
  { value: 'THEA', label: 'Theatre', collection: 'Theatre Ephemera' },
  { value: 'MAPS', label: 'Map', collection: 'Map Collection' },
  { value: 'TICK', label: 'Ticket/Stub', collection: 'Ticket Collection' },
  { value: 'MAGS', label: 'Magazine', collection: 'Vintage Magazine Collection' }
];

// Location codes for cathedrals/castles
const UK_LOCATIONS = [
  { code: 'ARMA', name: 'Armagh' },
  { code: 'BATH', name: 'Bath' },
  { code: 'BELF', name: 'Belfast' },
  { code: 'BRIS', name: 'Bristol' },
  { code: 'CANT', name: 'Canterbury' },
  { code: 'CARL', name: 'Carlisle' },
  { code: 'CHES', name: 'Chester' },
  { code: 'CHIC', name: 'Chichester' },
  { code: 'COVE', name: 'Coventry' },
  { code: 'DURH', name: 'Durham' },
  { code: 'EDIN', name: 'Edinburgh' },
  { code: 'ELY', name: 'Ely' },
  { code: 'EXET', name: 'Exeter' },
  { code: 'GLAS', name: 'Glasgow' },
  { code: 'GLOU', name: 'Gloucester' },
  { code: 'HAMP', name: 'Hampton Court' },
  { code: 'HERE', name: 'Hereford' },
  { code: 'LEIC', name: 'Leicester' },
  { code: 'LICH', name: 'Lichfield' },
  { code: 'LINC', name: 'Lincoln' },
  { code: 'LIVE', name: 'Liverpool' },
  { code: 'MANC', name: 'Manchester' },
  { code: 'NORW', name: 'Norwich' },
  { code: 'OXFO', name: 'Oxford' },
  { code: 'PETE', name: 'Peterborough' },
  { code: 'RIPO', name: 'Ripon' },
  { code: 'ROCH', name: 'Rochester' },
  { code: 'SALI', name: 'Salisbury' },
  { code: 'SHEF', name: 'Sheffield' },
  { code: 'SOUT', name: 'Southwark' },
  { code: 'STIR', name: 'Stirling' },
  { code: 'STPA', name: "St Paul's" },
  { code: 'TOWE', name: 'Tower of London' },
  { code: 'WARW', name: 'Warwick' },
  { code: 'WELL', name: 'Wells' },
  { code: 'WEST', name: 'Westminster' },
  { code: 'WINC', name: 'Winchester' },
  { code: 'WIND', name: 'Windsor' },
  { code: 'WORC', name: 'Worcester' },
  { code: 'YORK', name: 'York' }
];

// Garden locations (UK and US)
const GARDEN_LOCATIONS = [
  { code: 'KEEW', name: 'Kew Gardens' },
  { code: 'SISS', name: 'Sissinghurst' },
  { code: 'HIDC', name: 'Hidcote' },
  { code: 'WISL', name: 'RHS Wisley' },
  { code: 'EDEN', name: 'Eden Project' },
  { code: 'LONG', name: 'Longwood Gardens' },
  { code: 'HUNT', name: 'Huntington Gardens' },
  { code: 'BROO', name: 'Brooklyn Botanic' },
  { code: 'CHIB', name: 'Chicago Botanic' }
];

// Theatre locations
const THEATRE_LOCATIONS = [
  { code: 'WEND', name: 'West End' },
  { code: 'GLOB', name: "Shakespeare's Globe" },
  { code: 'NATL', name: 'National Theatre' },
  { code: 'BWAY', name: 'Broadway' },
  { code: 'RSCM', name: 'Royal Shakespeare Company' }
];

// Magazine publication codes
const MAGAZINE_PUBLICATIONS = [
  { code: 'NATGEO', name: 'National Geographic' },
  { code: 'LIFE', name: 'Life Magazine' },
  { code: 'TIME', name: 'Time Magazine' },
  { code: 'LOOK', name: 'Look Magazine' },
  { code: 'POST', name: 'Saturday Evening Post' }
];

// Format types for ephemera
const EPHEMERA_FORMATS = [
  'Pamphlet',
  'Brochure',
  'Booklet',
  'Guide',
  'Map',
  'Folding Map',
  'Postcard Set',
  'Souvenir Program',
  'Program',
  'Leaflet',
  'Flyer',
  'Poster (folded)',
  'Newsletter',
  'Catalog',
  'Ticket Stub',
  'Admission Ticket',
  'Magazine Issue',
  'Journal'
];

// Condition options
const CONDITION_OPTIONS = [
  { value: 'Excellent', description: 'Like new, no visible wear' },
  { value: 'Good', description: 'Minor wear, fully intact' },
  { value: 'Fair', description: 'Moderate wear, some creasing or minor damage' },
  { value: 'Poor', description: 'Significant wear, damage, or missing parts' }
];

// Genre/Subject templates for different categories
const SUBJECT_TEMPLATES = {
  CATH: ['Cathedrals--England', 'Gothic architecture--England', 'Church architecture--England', 'Religious buildings'],
  CAST: ['Castles--England', 'Fortification--England', 'Historic buildings--England', 'Military architecture'],
  PRIO: ['Priories--England', 'Monasteries--England', 'Religious architecture', 'Medieval buildings'],
  ABBE: ['Abbeys--England', 'Monasteries--England', 'Religious architecture', 'Medieval buildings'],
  CHUR: ['Churches--England', 'Church architecture--England', 'Religious buildings', 'Parish churches'],
  HIST: ['Historic sites--England', 'Historic buildings', 'Cultural heritage', 'Tourism'],
  GARD: [
    'Gardens--England.',
    'Gardens--United States.',
    'Landscape architecture.',
    'Botanical gardens.',
    'Historic gardens.'
  ],
  THEA: [
    'Theater--England--London.',
    'Theater programs.',
    'Performing arts.',
    'Drama.',
    'West End (London, England)'
  ],
  MAPS: [
    'Maps.',
    'Cartography.',
    'Geography.',
    'Tourist maps.',
    'Historical geography.'
  ],
  TICK: [
    'Tickets.',
    'Admission tickets.',
    'Souvenirs (Keepsakes).',
    'Ephemera.',
    'Entertainment.'
  ],
  MAGS: [
    'National geographic.',
    'Periodicals.',
    'Geography--Periodicals.',
    'Natural history--Periodicals.',
    'Vintage periodicals.'
  ]
};

// ==================== HELPER FUNCTIONS ====================

// Get location list based on category
const getLocationsForCategory = (category) => {
  switch (category) {
    case 'GARD':
      return GARDEN_LOCATIONS;
    case 'THEA':
      return THEATRE_LOCATIONS;
    case 'MAPS':
    case 'TICK':
    case 'MAGS':
      return []; // These use custom locations only
    default:
      return UK_LOCATIONS;
  }
};

// Generate accession number
// For MAGS category: uses MAG-[PUBLICATION]-[YEAR]-[ISSUE] format
// For all others: uses EPHEM-[CATEGORY]-[LOCATION]-[SEQ] format
const generateAccessionNumber = (category, locationCode, sequenceNum, magazineInfo = null) => {
  if (category === 'MAGS' && magazineInfo) {
    const { publication, year, issue } = magazineInfo;
    return `MAG-${publication}-${year}-${String(issue).padStart(2, '0')}`;
  }
  return `EPHEM-${category}-${locationCode}-${String(sequenceNum).padStart(3, '0')}`;
};

// Generate call number for special collections
const generateEphemeraCallNumber = (category, locationCode, sequenceNum, magazineInfo = null) => {
  if (category === 'MAGS' && magazineInfo) {
    const { publication, year, issue } = magazineInfo;
    return `SC MAG-${publication}-${year}-${String(issue).padStart(2, '0')}`;
  }
  return `SC EPHEM-${category}-${locationCode}-${String(sequenceNum).padStart(3, '0')}`;
};

// Convert text to proper Title Case
const toTitleCase = (str) => {
  if (!str) return '';
  const smallWords = new Set([
    'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'nor', 'of', 
    'on', 'or', 'so', 'the', 'to', 'up', 'yet', 'is', 'be', 'if', 'than'
  ]);
  
  return str
    .toLowerCase()
    .split(' ')
    .map((word, index) => {
      const cleanWord = word.replace(/:$/, '');
      const endsWithColon = word.endsWith(':');
      
      let result;
      if (index === 0 || !smallWords.has(cleanWord)) {
        result = cleanWord.charAt(0).toUpperCase() + cleanWord.slice(1);
      } else {
        result = cleanWord;
      }
      
      return endsWithColon ? result + ':' : result;
    })
    .join(' ');
};

// ==================== MAIN COMPONENT ====================
function MimirsWell() {
  const navigate = useNavigate();
  const { currentUser, userData, logout } = useAuth();
  
  // Form state
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [publisher, setPublisher] = useState('');
  const [publicationDate, setPublicationDate] = useState('');
  const [publicationPlace, setPublicationPlace] = useState('');
  const [category, setCategory] = useState('CATH');
  const [locationCode, setLocationCode] = useState('');
  const [customLocationCode, setCustomLocationCode] = useState('');
  const [ephemeraFormat, setEphemeraFormat] = useState('Pamphlet');
  const [pageCount, setPageCount] = useState('');
  const [dimensions, setDimensions] = useState('');
  const [condition, setCondition] = useState('Excellent');
  const [conditionNotes, setConditionNotes] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [customSubject, setCustomSubject] = useState('');
  const [notes, setNotes] = useState('');
  const [siteName, setSiteName] = useState(''); // e.g., "Lincoln Cathedral"

  // Magazine-specific fields
  const [magPublication, setMagPublication] = useState('NATGEO'); // Publication code
  const [magYear, setMagYear] = useState('');
  const [magIssue, setMagIssue] = useState(''); // Month number (01-12)

  // Batch management
  const [batch, setBatch] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentBarcode, setCurrentBarcode] = useState(200001); // Start ephemera barcodes at 200001
  const [sequenceNumbers, setSequenceNumbers] = useState({}); // Track sequence per category-location
  
  // UI state
  const [editingIndex, setEditingIndex] = useState(null);
  const batchEndRef = useRef(null);
  
  // LocalStorage keys
  const BATCH_STORAGE_KEY = `heimdl_ephemera_batch_${currentUser?.uid}`;
  const SEQUENCE_STORAGE_KEY = `heimdl_ephemera_sequences_${currentUser?.uid}`;

  // ==================== EFFECTS ====================
  
  useEffect(() => {
    if (currentUser) {
      loadBatchFromStorage();
      loadSequenceNumbers();
      loadBarcodeFromFirebase();
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && batch.length > 0) {
      saveBatchToStorage();
    }
  }, [batch, currentUser]);

  useEffect(() => {
    if (batch.length > 0 && batchEndRef.current) {
      batchEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [batch.length]);

  // Auto-populate subjects and reset location when category changes
  useEffect(() => {
    if (category && SUBJECT_TEMPLATES[category]) {
      setSubjects(SUBJECT_TEMPLATES[category]);
    }
    // Reset location when category changes since lists differ
    setLocationCode('');
    setCustomLocationCode('');
    setSiteName('');
  }, [category]);

  // Auto-generate site name from location and category
  useEffect(() => {
    if (locationCode && locationCode !== 'OTHER' && category) {
      const locations = getLocationsForCategory(category);
      const location = locations.find(loc => loc.code === locationCode);
      if (location) {
        const categoryObj = EPHEMERA_CATEGORIES.find(cat => cat.value === category);
        const categoryLabel = categoryObj?.label || '';
        setSiteName(`${location.name} ${categoryLabel}`);
      }
    }
  }, [locationCode, category]);

  // Auto-generate publisher from site name if not set
  useEffect(() => {
    if (siteName && !publisher) {
      setPublisher(siteName);
    }
  }, [siteName]);

  // ==================== STORAGE FUNCTIONS ====================
  
  const saveBatchToStorage = () => {
    try {
      localStorage.setItem(BATCH_STORAGE_KEY, JSON.stringify({
        batch: batch,
        currentBarcode: currentBarcode,
        timestamp: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error saving batch:', error);
    }
  };

  const loadBatchFromStorage = () => {
    try {
      const saved = localStorage.getItem(BATCH_STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        setBatch(data.batch || []);
        if (data.currentBarcode) {
          setCurrentBarcode(data.currentBarcode);
        }
      }
    } catch (error) {
      console.error('Error loading batch:', error);
    }
  };

  const loadSequenceNumbers = async () => {
    try {
      // Load from localStorage first
      const saved = localStorage.getItem(SEQUENCE_STORAGE_KEY);
      if (saved) {
        setSequenceNumbers(JSON.parse(saved));
      }
      
      // Also check Firebase for any existing ephemera to get accurate counts
      if (currentUser) {
        const ephemeraRef = collection(db, 'users', currentUser.uid, 'ephemera');
        const snapshot = await getDocs(ephemeraRef);
        
        const counts = {};
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const key = `${data.category}-${data.locationCode}`;
          counts[key] = Math.max(counts[key] || 0, data.sequenceNumber || 0);
        });
        
        setSequenceNumbers(prev => ({ ...prev, ...counts }));
      }
    } catch (error) {
      console.error('Error loading sequence numbers:', error);
    }
  };

  const saveSequenceNumbers = (sequences) => {
    try {
      localStorage.setItem(SEQUENCE_STORAGE_KEY, JSON.stringify(sequences));
    } catch (error) {
      console.error('Error saving sequence numbers:', error);
    }
  };

  const loadBarcodeFromFirebase = async () => {
    if (!currentUser) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const lastEphemeraBarcode = userDoc.data().lastEphemeraBarcodeUsed;
        if (lastEphemeraBarcode) {
          setCurrentBarcode(lastEphemeraBarcode + 1);
        }
      }
    } catch (error) {
      console.error('Error loading barcode:', error);
    }
  };

  const incrementBarcodeInFirebase = async (usedBarcode) => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        lastEphemeraBarcodeUsed: usedBarcode,
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error('Error updating barcode:', error);
    }
  };

  // ==================== MAIN HANDLERS ====================
  
  const getNextSequenceNumber = (cat, loc) => {
    const key = `${cat}-${loc}`;
    const current = sequenceNumbers[key] || 0;
    const next = current + 1;
    
    const updated = { ...sequenceNumbers, [key]: next };
    setSequenceNumbers(updated);
    saveSequenceNumbers(updated);
    
    return next;
  };

  const handleAddItem = async () => {
    // Validation differs for magazines vs other ephemera
    if (category === 'MAGS') {
      if (!title.trim() || !magYear || !magIssue) {
        alert('Please enter title, year, and issue number for magazines');
        return;
      }
    } else {
      if (!title.trim() || !siteName.trim()) {
        alert('Please enter at least a title and site name');
        return;
      }

      const effectiveLocationCode = locationCode === 'OTHER' ? customLocationCode.toUpperCase() : locationCode;

      if (!effectiveLocationCode) {
        alert('Please select or enter a location code');
        return;
      }
    }

    setLoading(true);

    try {
      const effectiveLocationCode = locationCode === 'OTHER' ? customLocationCode.toUpperCase() : locationCode;

      // For magazines, use different accession number format
      let accessionNumber, callNumber, sequenceNum;
      if (category === 'MAGS') {
        const magazineInfo = { publication: magPublication, year: magYear, issue: magIssue };
        accessionNumber = generateAccessionNumber(category, effectiveLocationCode, 0, magazineInfo);
        callNumber = generateEphemeraCallNumber(category, effectiveLocationCode, 0, magazineInfo);
        sequenceNum = parseInt(magIssue);
      } else {
        sequenceNum = getNextSequenceNumber(category, effectiveLocationCode);
        accessionNumber = generateAccessionNumber(category, effectiveLocationCode, sequenceNum);
        callNumber = generateEphemeraCallNumber(category, effectiveLocationCode, sequenceNum);
      }
      
      // Build physical description
      let physicalDesc = '';
      if (pageCount) physicalDesc += `${pageCount} pages`;
      if (dimensions) {
        physicalDesc += physicalDesc ? ` ; ${dimensions}` : dimensions;
      }
      if (!physicalDesc) physicalDesc = ephemeraFormat;

      // Get collection name from category
      const categoryInfo = EPHEMERA_CATEGORIES.find(c => c.value === category);
      const collectionName = categoryInfo?.collection || 'Special Collections';

      // Build subjects - different for magazines vs other ephemera
      let allSubjects;
      if (category === 'MAGS') {
        const pubInfo = MAGAZINE_PUBLICATIONS.find(p => p.code === magPublication);
        allSubjects = [`${pubInfo?.name || magPublication}.`, ...subjects];
      } else {
        const siteSubject = `${siteName} (England)`;
        allSubjects = [siteSubject, ...subjects.filter(s => s !== siteSubject)];
      }

      // Magazine-specific title and metadata
      const isMagazine = category === 'MAGS';
      const pubInfo = isMagazine ? MAGAZINE_PUBLICATIONS.find(p => p.code === magPublication) : null;
      const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const issueMonth = isMagazine ? monthNames[parseInt(magIssue)] || '' : '';

      const newItem = {
        // Identifiers
        accessionNumber,
        barcode: currentBarcode.toString(),
        callNumber,

        // Basic info
        title: isMagazine
          ? `${pubInfo?.name || magPublication}, ${issueMonth} ${magYear}`
          : toTitleCase(title.trim()),
        author: author.trim() || null,
        publisher: isMagazine
          ? (pubInfo?.name || magPublication)
          : (publisher.trim() || siteName.trim()),
        publicationDate: isMagazine ? magYear : (publicationDate || '[date unknown]'),
        publicationPlace: isMagazine
          ? 'Washington, D.C.'
          : (publicationPlace.trim() || `${siteName}, England`),

        // Classification
        category,
        categoryLabel: categoryInfo?.label,
        locationCode: isMagazine ? magPublication : effectiveLocationCode,
        siteName: isMagazine ? (pubInfo?.name || magPublication) : siteName.trim(),
        sequenceNumber: sequenceNum,

        // Magazine-specific fields
        ...(isMagazine && {
          magPublication,
          magYear,
          magIssue,
          issueMonth
        }),

        // Physical description
        ephemeraFormat: isMagazine ? 'Magazine' : ephemeraFormat,
        pageCount: pageCount || null,
        dimensions: dimensions || null,
        physicalDesc,

        // Condition
        condition,
        conditionNotes: conditionNotes.trim() || null,

        // Collection info
        collection: collectionName,
        provenance: isMagazine
          ? 'Kahn Vintage Magazine Collection; gift of Dr. Verity Kahn'
          : 'Kahn UK Cathedral Collection; gift of Dr. Verity Kahn',
        physicalLocation: `Special Collections - ${collectionName}`,

        // Subjects and genres
        subjects: allSubjects,
        genres: isMagazine ? ['Periodicals', 'Magazines'] : ['Pamphlets', 'Tourist literature'],

        // Access
        accessStatus: 'Non-circulating; Supervised use only; In-library use only',
        isCirculating: false,

        // Notes
        notes: notes.trim() || null,
        generalNotes: [
          `Part of the Holy Family ${collectionName} Special Collection.`,
          isMagazine
            ? 'Stored in magazine box with acid-free backing.'
            : 'Stored in polyester archival sleeve with acid-free backing board.'
        ],

        // Metadata
        itemType: isMagazine ? 'MAGAZINE' : 'EPHEMERA',
        status: 'pending',
        createdAt: new Date(),
        catalogedDate: new Date().toISOString().split('T')[0],
        
        // For MARC export compatibility
        language: 'eng',
        dataSources: ['Manual cataloging']
      };

      setBatch([...batch, newItem]);
      await incrementBarcodeInFirebase(currentBarcode);
      setCurrentBarcode(currentBarcode + 1);
      
      console.log(`✅ Ephemera added: ${accessionNumber}`);

      // Clear form (keep category and some defaults)
      setTitle('');
      setAuthor('');
      setPublicationDate('');
      setPublicationPlace('');
      setPageCount('');
      setDimensions('');
      setConditionNotes('');
      setNotes('');

      // For magazines, only clear issue-specific fields (keep publication and year for batch entry)
      if (category === 'MAGS') {
        setMagIssue('');
        // Keep: magPublication, magYear for faster batch entry of same year's issues
      }
      // Keep: category, locationCode, siteName, publisher, condition, ephemeraFormat, subjects

    } catch (error) {
      console.error('Error adding item:', error);
      alert('Error adding item: ' + error.message);
    }

    setLoading(false);
  };

  const removeItemFromBatch = (index) => {
    const item = batch[index];
    
    // Decrement sequence number for this category-location
    const key = `${item.category}-${item.locationCode}`;
    const updated = { ...sequenceNumbers };
    if (updated[key] && updated[key] > 0) {
      updated[key] = updated[key] - 1;
      setSequenceNumbers(updated);
      saveSequenceNumbers(updated);
    }
    
    setBatch(batch.filter((_, i) => i !== index));
  };

  const processBatch = async () => {
    if (batch.length === 0) return;

    setLoading(true);
    try {
      // Save to 'ephemera' collection (separate from books)
      const batchPromises = batch.map(item =>
        addDoc(collection(db, 'users', currentUser.uid, 'ephemera'), item)
      );

      // Also save to 'books' collection for unified MARC export
      const booksBatchPromises = batch.map(item =>
        addDoc(collection(db, 'users', currentUser.uid, 'books'), {
          ...item,
          // Ensure compatibility with existing MARC export
          isbn: null,
          isbn13: null,
          isbn10: null,
          deweyDecimal: null,
          listPrice: 0,
          libraryPrice: 0,
          location: 'Special Collections',
          targetAudience: null,
          readingLevel: null,
          lexileLevel: null,
          ficOrNonfic: 'Non-Fiction'
        })
      );

      await Promise.all([...batchPromises, ...booksBatchPromises]);

      const highestBarcode = Math.max(...batch.map(item => parseInt(item.barcode)));
      await updateDoc(doc(db, 'users', currentUser.uid), {
        lastEphemeraBarcodeUsed: highestBarcode,
        lastUpdated: new Date()
      });

      console.log(`✅ Batch processed: ${batch.length} ephemera items`);

      setBatch([]);
      localStorage.removeItem(BATCH_STORAGE_KEY);
      navigate('/asgard');

    } catch (error) {
      console.error('Error processing batch:', error);
      alert('Error processing batch: ' + error.message);
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const addCustomSubject = () => {
    if (customSubject.trim() && !subjects.includes(customSubject.trim())) {
      setSubjects([...subjects, customSubject.trim()]);
      setCustomSubject('');
    }
  };

  const removeSubject = (subjectToRemove) => {
    setSubjects(subjects.filter(s => s !== subjectToRemove));
  };

  // ==================== EDIT MODAL ====================
  const EditModal = ({ item, index }) => {
    const [editedItem, setEditedItem] = useState({ ...item });

    if (editingIndex !== index) return null;

    const updateItem = (field, value) => {
      setEditedItem(prev => ({ ...prev, [field]: value }));
    };

    const saveChanges = () => {
      const updatedBatch = [...batch];
      updatedBatch[index] = editedItem;
      setBatch(updatedBatch);
      setEditingIndex(null);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-lg max-w-4xl w-full my-8">
          <div className="sticky top-0 bg-white border-b px-6 py-4 rounded-t-lg">
            <h2 className="text-xl font-bold">Edit Ephemera Details</h2>
            <p className="text-sm text-gray-600">{item.title}</p>
          </div>

          <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Basic Info */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-3">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Title</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedItem.title}
                    onChange={(e) => updateItem('title', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Site Name</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedItem.siteName}
                    onChange={(e) => updateItem('siteName', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Author/Creator</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedItem.author || ''}
                    onChange={(e) => updateItem('author', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Publisher</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedItem.publisher}
                    onChange={(e) => updateItem('publisher', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Publication Date</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedItem.publicationDate}
                    onChange={(e) => updateItem('publicationDate', e.target.value)}
                    placeholder="[ca. 2000] or 2015"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Publication Place</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedItem.publicationPlace}
                    onChange={(e) => updateItem('publicationPlace', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Physical Description */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-3">Physical Description</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Format</label>
                  <select
                    className="w-full px-3 py-2 border rounded"
                    value={editedItem.ephemeraFormat}
                    onChange={(e) => updateItem('ephemeraFormat', e.target.value)}
                  >
                    {EPHEMERA_FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Pages</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedItem.pageCount || ''}
                    onChange={(e) => updateItem('pageCount', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Dimensions</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedItem.dimensions || ''}
                    onChange={(e) => updateItem('dimensions', e.target.value)}
                    placeholder="22 x 28 cm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Condition</label>
                  <select
                    className="w-full px-3 py-2 border rounded"
                    value={editedItem.condition}
                    onChange={(e) => updateItem('condition', e.target.value)}
                  >
                    {CONDITION_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.value}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium block mb-1">Condition Notes</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedItem.conditionNotes || ''}
                    onChange={(e) => updateItem('conditionNotes', e.target.value)}
                    placeholder="Minor creasing on cover"
                  />
                </div>
              </div>
            </div>

            {/* Cataloging */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-3">Cataloging</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Accession Number</label>
                  <input
                    className="w-full px-3 py-2 border rounded font-mono bg-gray-50"
                    value={editedItem.accessionNumber}
                    readOnly
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Call Number</label>
                  <input
                    className="w-full px-3 py-2 border rounded font-mono"
                    value={editedItem.callNumber}
                    onChange={(e) => updateItem('callNumber', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Collection</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedItem.collection}
                    onChange={(e) => updateItem('collection', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Physical Location</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedItem.physicalLocation}
                    onChange={(e) => updateItem('physicalLocation', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <h3 className="font-semibold mb-3">Notes</h3>
              <textarea
                className="w-full px-3 py-2 border rounded"
                rows="3"
                value={editedItem.notes || ''}
                onChange={(e) => updateItem('notes', e.target.value)}
                placeholder="Additional notes..."
              />
            </div>
          </div>

          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end space-x-3 rounded-b-lg">
            <button
              onClick={() => setEditingIndex(null)}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={saveChanges}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Edit Modal */}
      {editingIndex !== null && (
        <EditModal item={batch[editingIndex]} index={editingIndex} />
      )}

      {/* Header */}
      <header className="bg-gradient-to-r from-cyan-900 to-teal-900 text-white shadow-xl border-b border-cyan-700/50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size={40} showText={false} />
              <button onClick={() => navigate('/asgard')} className="text-lg sm:text-2xl font-bold hover:text-cyan-200 tracking-wider">
                HEIMDL
              </button>
              <span className="hidden sm:inline text-cyan-300">|</span>
              <span className="hidden sm:inline text-cyan-200">Mímir's Well</span>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="hidden md:block text-right">
                <p className="text-sm text-cyan-300">Current Barcode</p>
                <p className="text-xl font-mono font-bold">{currentBarcode}</p>
              </div>
              <button
                onClick={handleLogout}
                className="text-sm sm:text-base bg-cyan-700 hover:bg-cyan-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors"
              >
                Leave Asgard
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Panel */}
      <div className="max-w-[1600px] mx-auto px-4 pt-6">
        <div className="bg-cyan-50 rounded-xl shadow-xl p-4 border border-cyan-200 mb-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold text-cyan-900">Mímir's Well</h1>
              <p className="text-cyan-700 text-sm">Cathedral, Castle & Priory Special Collections</p>
            </div>
            <div className="flex space-x-2">
              <button onClick={() => navigate('/forge')} className="bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors text-sm">
                Books (Forge)
              </button>
              <button onClick={() => navigate('/runes')} className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 transition-colors text-sm">
                Labels
              </button>
              <button onClick={() => navigate('/bifrost')} className="bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-700 transition-colors text-sm">
                MARC Export
              </button>
              <button onClick={() => navigate('/allsight')} className="bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors text-sm">
                Browse All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-4 pb-6">
        <div className="flex gap-6">
          {/* LEFT COLUMN - Input Form */}
          <div className="w-3/5">
            <div className="bg-white rounded-xl shadow-xl p-5 border border-cyan-200 space-y-4">
              
              {/* Row 1: Category & Location (or Magazine fields) */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Category *</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                  >
                    {EPHEMERA_CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* Magazine-specific fields */}
                {category === 'MAGS' ? (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Publication *</label>
                      <select
                        value={magPublication}
                        onChange={(e) => setMagPublication(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                      >
                        {MAGAZINE_PUBLICATIONS.map(pub => (
                          <option key={pub.code} value={pub.code}>{pub.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">Year *</label>
                        <input
                          type="text"
                          maxLength={4}
                          value={magYear}
                          onChange={(e) => setMagYear(e.target.value.replace(/[^0-9]/g, ''))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="1955"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">Issue *</label>
                        <input
                          type="text"
                          maxLength={2}
                          value={magIssue}
                          onChange={(e) => setMagIssue(e.target.value.replace(/[^0-9]/g, ''))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="03"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Location *</label>
                      {locationCode === 'OTHER' || getLocationsForCategory(category).length === 0 ? (
                        <input
                          type="text"
                          maxLength={4}
                          value={customLocationCode}
                          onChange={(e) => setCustomLocationCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg uppercase"
                          placeholder="Type 4-letter code..."
                        />
                      ) : (
                        <select
                          value={locationCode}
                          onChange={(e) => setLocationCode(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                        >
                          <option value="">Select location...</option>
                          {getLocationsForCategory(category).map(loc => (
                            <option key={loc.code} value={loc.code}>{loc.name}</option>
                          ))}
                        </select>
                      )}
                      {locationCode !== 'OTHER' && getLocationsForCategory(category).length > 0 && (
                        <button
                          type="button"
                          onClick={() => { setLocationCode('OTHER'); setCustomLocationCode(''); setSiteName(''); }}
                          className="text-xs text-cyan-600 hover:text-cyan-800 mt-1"
                        >
                          Custom location?
                        </button>
                      )}
                      {locationCode === 'OTHER' && (
                        <button
                          type="button"
                          onClick={() => { setLocationCode(''); setCustomLocationCode(''); }}
                          className="text-xs text-cyan-600 hover:text-cyan-800 mt-1"
                        >
                          ← Back to list
                        </button>
                      )}
                    </div>
                    <div className={locationCode === 'OTHER' ? '' : 'col-span-1'}>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Site Name *</label>
                      <input
                        type="text"
                        value={siteName}
                        onChange={(e) => setSiteName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="e.g., Lincoln Cathedral"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Row 2: Title & Author */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Title *</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Exploring Lincoln Cathedral"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Author/Creator</label>
                  <input
                    type="text"
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Last, First (if known)"
                  />
                </div>
              </div>

              {/* Row 3: Publisher & Date */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Publisher</label>
                  <input
                    type="text"
                    value={publisher}
                    onChange={(e) => setPublisher(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Usually the cathedral/castle"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Publication Date</label>
                  <input
                    type="text"
                    value={publicationDate}
                    onChange={(e) => setPublicationDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="2015 or [ca. 2000]"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Publication Place</label>
                  <input
                    type="text"
                    value={publicationPlace}
                    onChange={(e) => setPublicationPlace(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Lincoln, England"
                  />
                </div>
              </div>

              {/* Row 4: Physical Description */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Format</label>
                  <select
                    value={ephemeraFormat}
                    onChange={(e) => setEphemeraFormat(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                  >
                    {EPHEMERA_FORMATS.map(f => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Pages</label>
                  <input
                    type="text"
                    value={pageCount}
                    onChange={(e) => setPageCount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="24"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Dimensions</label>
                  <input
                    type="text"
                    value={dimensions}
                    onChange={(e) => setDimensions(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="22 x 28 cm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Condition</label>
                  <select
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                  >
                    {CONDITION_OPTIONS.map(c => (
                      <option key={c.value} value={c.value}>{c.value} - {c.description}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 5: Condition Notes */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Condition Notes</label>
                <input
                  type="text"
                  value={conditionNotes}
                  onChange={(e) => setConditionNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Minor creasing on cover, small tear on back page..."
                />
              </div>

              {/* Row 6: Subjects */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Subject Headings</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {subjects.map((subject, idx) => (
                    <span key={idx} className="bg-cyan-100 text-cyan-800 px-2 py-1 rounded text-sm flex items-center">
                      {subject}
                      <button
                        onClick={() => removeSubject(subject)}
                        className="ml-1 text-cyan-600 hover:text-cyan-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customSubject}
                    onChange={(e) => setCustomSubject(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addCustomSubject()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Add custom subject..."
                  />
                  <button
                    onClick={addCustomSubject}
                    className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Row 7: Notes */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Additional Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="2"
                  placeholder="Any additional cataloging notes..."
                />
              </div>

              {/* Add Button */}
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={handleAddItem}
                  disabled={loading || !title.trim() || (category === 'MAGS'
                    ? (!magYear || !magIssue)
                    : (!siteName.trim() || (!locationCode && !customLocationCode))
                  )}
                  className="w-full bg-cyan-600 text-white py-3 rounded-lg hover:bg-cyan-700 disabled:bg-gray-400 font-medium text-lg"
                >
                  {loading ? 'Adding...' : 'Add to Batch'}
                </button>
              </div>

              {/* Preview - Magazine format */}
              {category === 'MAGS' && title && magYear && magIssue && (
                <div className="mt-4 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <h4 className="text-sm font-semibold text-cyan-900 mb-2">Preview:</h4>
                  <p className="font-mono text-sm text-cyan-800">
                    {generateAccessionNumber(category, '', 0, { publication: magPublication, year: magYear, issue: magIssue })}
                  </p>
                  <p className="text-sm text-cyan-700 mt-1">
                    → {EPHEMERA_CATEGORIES.find(c => c.value === category)?.collection}
                  </p>
                </div>
              )}

              {/* Preview - Standard ephemera format */}
              {category !== 'MAGS' && title && siteName && (locationCode || customLocationCode) && (
                <div className="mt-4 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <h4 className="text-sm font-semibold text-cyan-900 mb-2">Preview:</h4>
                  <p className="font-mono text-sm text-cyan-800">
                    {generateAccessionNumber(
                      category,
                      locationCode === 'OTHER' ? customLocationCode : locationCode,
                      (sequenceNumbers[`${category}-${locationCode === 'OTHER' ? customLocationCode : locationCode}`] || 0) + 1
                    )}
                  </p>
                  <p className="text-sm text-cyan-700 mt-1">
                    → {EPHEMERA_CATEGORIES.find(c => c.value === category)?.collection} acrylic file
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN - Batch */}
          <div className="w-2/5">
            <div className="bg-white rounded-xl shadow-xl border border-cyan-200 sticky top-6 flex flex-col" style={{ maxHeight: 'calc(100vh - 140px)' }}>
              {/* Header */}
              <div className="px-4 py-3 border-b border-cyan-200 bg-gradient-to-r from-cyan-100 to-teal-100 rounded-t-xl flex-shrink-0">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold text-cyan-900">
                    Batch {batch.length > 0 && <span className="text-cyan-600">({batch.length})</span>}
                  </h2>
                  {batch.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={processBatch}
                        disabled={loading}
                        className="bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 disabled:bg-gray-400 text-sm font-medium"
                      >
                        Process
                      </button>
                      <button
                        onClick={() => {
                          setBatch([]);
                          localStorage.removeItem(BATCH_STORAGE_KEY);
                        }}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Item List */}
              <div className="flex-1 overflow-y-auto">
                {batch.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <div className="text-4xl mb-3">💧</div>
                    <p className="font-medium">Mímir's Well awaits</p>
                    <p className="text-sm mt-1">Add ephemera to preserve their wisdom</p>
                  </div>
                ) : (
                  <div className="divide-y divide-cyan-100">
                    {batch.map((item, index) => (
                      <div key={index} className="px-3 py-2 hover:bg-cyan-50">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs font-bold text-cyan-600">
                                {item.accessionNumber}
                              </span>
                            </div>
                            <div className="font-medium text-sm truncate" title={item.title}>
                              {item.title}
                            </div>
                            <div className="text-xs text-gray-600">
                              {item.siteName} • {item.ephemeraFormat}
                            </div>
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-cyan-700 font-mono bg-cyan-50 px-1.5 py-0.5 rounded text-xs">
                                {item.callNumber}
                              </span>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                item.condition === 'Excellent' ? 'bg-green-100 text-green-700' :
                                item.condition === 'Good' ? 'bg-blue-100 text-blue-700' :
                                item.condition === 'Fair' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {item.condition}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <button
                              onClick={() => setEditingIndex(index)}
                              className="bg-blue-500 text-white px-2 py-0.5 rounded text-xs hover:bg-blue-600"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => removeItemFromBatch(index)}
                              className="text-red-500 hover:text-red-700 text-xs"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={batchEndRef} />
                  </div>
                )}
              </div>

              {/* Summary */}
              {batch.length > 0 && (
                <div className="px-4 py-3 border-t border-cyan-200 bg-cyan-50 rounded-b-xl text-sm">
                  <div className="flex justify-between text-cyan-800">
                    <span>Cathedrals: {batch.filter(i => i.category === 'CATH').length}</span>
                    <span>Castles: {batch.filter(i => i.category === 'CAST').length}</span>
                    <span>Other: {batch.filter(i => !['CATH', 'CAST'].includes(i.category)).length}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default MimirsWell;